import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type AbilitySummary } from '@apm/contracts';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PromptPanel } from '../../features/ai-console/components/prompt-panel';
import { ComparePanel } from '../../features/harness/components/compare-panel';
import { DirectComparePanel } from '../../features/harness/components/direct-compare-panel';
import { MetricsPanel } from '../../features/harness/components/metrics-panel';
import { ModelsPanel } from '../../features/harness/components/models-panel';
import { RegistryPanel } from '../../features/harness/components/registry-panel';
import { RunPanel } from '../../features/harness/components/run-panel';
import { apiFetch } from '../../lib/http';

const TABS = ['run', 'direct', 'compare', 'registry', 'prompts', 'metrics', 'models'] as const;
type Tab = (typeof TABS)[number];
const Search = z.object({
  tab: z.enum(TABS).optional().catch(undefined),
  /** Mở sẵn một run ở tab So hai run — link từ thư viện bài học (APM-H-ao). */
  run: z.uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/system_/ai-harness')({
  component: HarnessPage,
  validateSearch: Search,
});

/**
 * Trang Harness — APM-H-h (`plan-ai-features.md` §11, `plan-harness.md` §11).
 * Bốn khung: Chạy thử (skill → params → dự án → mode → sáu cột dấu vết qua SSE +
 * bảng usage), So hai run, Registry, Prompt (dùng lại tab của `/system/ai`), Chỉ số.
 * Ẩn khi thiếu `ai_harness:use`; thuật ngữ kỹ thuật (tên tool, bước) không i18n.
 */
function HarnessPage(): ReactNode {
  const { t } = useTranslation();
  const { tab, run } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [compareLeft, setCompareLeft] = useState<string | null>(run ?? null);
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    staleTime: 5 * 60_000,
  });
  if (abilities.isPending) return null;
  if (!abilities.data?.global.includes('ai_harness:use')) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">{t('harness.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('harness.forbidden')}</p>
        <Link to="/" className="text-sm underline">
          {t('nav.dashboard')}
        </Link>
      </div>
    );
  }
  // Tab Prompt cần `ai_config:manage`. Cả hai quyền nay chỉ SUPER_ADMIN có (APM-H-t2 lật lại APM-H-s,
  // nơi ORG_ADMIN từng có `ai_harness:use`) — lọc vẫn giữ để hai quyền không bị buộc vào nhau.
  const canManage = abilities.data.global.includes('ai_config:manage');
  const tabs = TABS.filter((id) => id !== 'prompts' || canManage);
  const current: Tab = tab && tabs.includes(tab) ? tab : 'run';

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('harness.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('harness.subtitle')}</p>
      </div>
      <Tabs
        value={current}
        onValueChange={(value) => void navigate({ search: { tab: value as Tab } })}
      >
        <TabsList aria-label={t('harness.title')}>
          {tabs.map((id) => (
            <TabsTrigger key={id} value={id}>
              {t(`harness.tabs.${id}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {current === 'run' ? (
        <RunPanel
          onOpenRun={(runId) => {
            setCompareLeft(runId);
            void navigate({ search: { tab: 'compare' } });
          }}
        />
      ) : null}
      {current === 'direct' ? <DirectComparePanel /> : null}
      {current === 'compare' ? <ComparePanel initialLeft={compareLeft} /> : null}
      {current === 'registry' ? <RegistryPanel /> : null}
      {current === 'prompts' ? <PromptPanel /> : null}
      {current === 'metrics' ? <MetricsPanel /> : null}
      {current === 'models' ? <ModelsPanel /> : null}
    </div>
  );
}
